import i18n from '@i18next/i18n';
import { IShapeModelStyle } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/shape-model-props.interface';
import { ILineCap } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { ISolidFill } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { LineEndType, LineEndWidthType } from '@src/slide/infrastructure/module/dependent/line-info/endStyle';
import { LineJoin } from '@tencent/alloy-renderer';
import { CompoundType, GradientType } from '@tencent/alloy-renderer-adapter';
import { ICTShapeStyle, STSchemeColorVal } from '@tencent/slide-sdk-model';

export enum ConnectorFastStyleType {
    subtle,
    moderate,
    intense,
    dash,
    singleArrow,
    doubleArrow,
}
export interface IShapeStyles {
    fastStyle: ICTShapeStyle;
    style?: Partial<IShapeModelStyle>
}
export interface IShapeFastStyleConfig extends IShapeStyles {
    title: string;
}
export interface IConnectorFastStyleConfig extends IShapeStyles {
    title: string;
    type: ConnectorFastStyleType;
}

const colorList = [
    {
        val: STSchemeColorVal.dk1,
        name: i18n.t('深色{0}', { 0: 1 }),
    },
    {
        val: STSchemeColorVal.accent1,
        name: i18n.t('强调颜色{0}', { 0: 1 }),
    },
    {
        val: STSchemeColorVal.accent2,
        name: i18n.t('强调颜色{0}', { 0: 2 }),
    },
    {
        val: STSchemeColorVal.accent3,
        name: i18n.t('强调颜色{0}', { 0: 3 }),
    },
    {
        val: STSchemeColorVal.accent4,
        name: i18n.t('强调颜色{0}', { 0: 4 }),
    },
    {
        val: STSchemeColorVal.accent5,
        name: i18n.t('强调颜色{0}', { 0: 5 }),
    },
    {
        val: STSchemeColorVal.accent6,
        name: i18n.t('强调颜色{0}', { 0: 6 }),
    },
];

// 下面注释的样式目前还原还有问题
export const ShapeFastStyleConfigs: IShapeFastStyleConfig[] = [
    // 彩色轮廓
    ...colorList.map(color => ({
        title: `${i18n.t('彩色轮廓')} ${color.name}`,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.dk1,
                    },
                },
                idx: 0,
            },
            fillRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.lt1,
                    },
                },
                idx: 1,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.dk1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 2,
            },
        },
    })),
    // 彩色填充
    ...colorList.map(color => ({
        title: `${i18n.t('彩色填充')} ${color.name}`,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 0,
            },
            fillRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 1,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.lt1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.dk1,
                    },
                    egColorTransform: [{ shade: { val: 15000 } }],
                },
                idx: 2,
            },
        },
    })),
    // 浅色轮廓，彩色填充
    ...colorList.map(color => ({
        title: `${i18n.t('浅色轮廓')}${i18n.t('，')}${i18n.t('彩色填充')} ${color.name}`,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 1,
            },
            fillRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 1,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.lt1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.lt1,
                    },
                },
                idx: 3,
            },
        },
    })),
    // 细微效果
    ...colorList.map(color => ({
        title: `${i18n.t('细微效果')} ${color.name}`,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 1,
            },
            fillRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 2,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.dk1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 1,
            },
        },
    })),
    // 中等效果
    ...colorList.map(color => ({
        title: `${i18n.t('中等效果')} ${color.name}`,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 2,
            },
            fillRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 3,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.lt1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 1,
            },
        },
    })),
    // 强烈效果
    ...colorList.map(color => ({
        title: `${i18n.t('强烈效果')} ${color.name}`,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 3,
            },
            fillRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 3,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.lt1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 0,
            },
        },
    })),
    // 透明
    ...colorList.map(color => ({
        title: `${i18n.t('透明')} ${color.name}`,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fillRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
        },
        style: {
            border: { noFill: 1 },
            fillBackground: { noFill: 1 },
        },
    })),
    // 透明，彩色轮廓
    ...colorList.map(color => ({
        title: `${i18n.t('透明')}${i18n.t('，')}${i18n.t('彩色轮廓')} ${color.name}`,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fillRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
        },
        style: {
            border: {
                width: 1.5,
                linecap: ILineCap.Butt,
                style: 'solid',
                linejoin: LineJoin.Round,
                tdColor: new TDColor({ themeKey: color.val }),
                compoundType: CompoundType.SINGLE,
            },
            fillBackground: { noFill: 1 },
        },
    })),
    // 半透明，无轮廓
    ...colorList.map(color => ({
        title: `${i18n.t('半透明')}${i18n.t('，')}${i18n.t('无轮廓')} ${color.name}`,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fillRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.lt1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
        },
        style: {
            border: { noFill: 1 },
            fillBackground: {
                solidFill: {
                    color: new TDColor({ themeKey: color.val, alpha: 0.5 }),
                },
            },
        },
    })),
    // 彩色填充，无轮廓
    ...colorList.map(color => ({
        title: `${i18n.t('彩色填充')}${i18n.t('，')}${i18n.t('无轮廓')} ${color.name}`,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fillRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.lt1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
        },
        style: {
            border: { noFill: 1 },
            fillBackground: {
                solidFill: {
                    color: new TDColor({ themeKey: color.val }),
                },
            },
        },
    })),
    // 渐变填充，无轮廓
    ...colorList.map(color => ({
        title: `${i18n.t('渐变填充')}${i18n.t('，')}${i18n.t('无轮廓')} ${color.name}`,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fillRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.lt1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
        },
        style: {
            border: { noFill: 1 },
            fillBackground: {
                gradFill: {
                    gsList: [
                        { pos: 0, color: new TDColor({ themeKey: color.val, lumMod: 67000 }) },
                        { pos: 48, color: new TDColor({ themeKey: color.val, lumMod: 97000, lumOff: 3000 }) },
                        { pos: 100, color: new TDColor({ themeKey: color.val, lumMod: 60000, lumOff: 40000 }) },
                    ],
                    lin: {
                        ang: 270,
                        scaled: 1,
                    },
                    pathGradient: {
                        path: GradientType.Linear,
                    },
                    tileRect: { b: 0, l: 0, r: 0, t: 0 },
                },
            },
        },
    })),
];

export const ConnectorFastStyleConfigs: IConnectorFastStyleConfig[] = [
    ...colorList.map(color => ({
        title: `${i18n.t('细线')} ${color.name}`,
        type: ConnectorFastStyleType.subtle,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 0,
            },
            fillRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 0,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 0,
            },
            lnRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 1,
            },
        },
    })),
    ...colorList.map(color => ({
        title: `${i18n.t('中等线')} ${color.name}`,
        type: ConnectorFastStyleType.moderate,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 1,
            },
            fillRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 0,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.tx1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 2,
            },
        },
    })),
    ...colorList.map(color => ({
        title: `${i18n.t('粗线')} ${color.name}`,
        type: ConnectorFastStyleType.intense,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 2,
            },
            fillRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 0,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.tx1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    schemeClr: {
                        val: color.val,
                    },
                },
                idx: 3,
            },
        },
    })),
    ...colorList.map(color => ({
        title: `${i18n.t('虚线')} ${color.name}`,
        type: ConnectorFastStyleType.dash,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fillRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.tx1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
        },
        style: {
            border: {
                width: 3,
                linecap: ILineCap.Butt,
                style: 'dash',
                linejoin: LineJoin.Round,
                tdColor: new TDColor({ themeKey: color.val }),
                headEnd: {
                    lineEndType: LineEndType.NONE,
                    lengthType: LineEndWidthType.MIDDLE,
                    widthType: LineEndWidthType.MIDDLE,
                },
                tailEnd: {
                    lineEndType: LineEndType.NONE,
                    lengthType: LineEndWidthType.MIDDLE,
                    widthType: LineEndWidthType.MIDDLE,
                },
                compoundType: CompoundType.SINGLE,
            },
        },
    })),
    ...colorList.map(color => ({
        title: `${i18n.t('单箭头')} ${color.name}`,
        type: ConnectorFastStyleType.singleArrow,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fillRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.tx1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
        },
        style: {
            border: {
                width: 3,
                linecap: ILineCap.Butt,
                style: 'solid',
                linejoin: LineJoin.Round,
                tdColor: new TDColor({ themeKey: color.val }),
                headEnd: {
                    lineEndType: LineEndType.NONE,
                    lengthType: LineEndWidthType.MIDDLE,
                    widthType: LineEndWidthType.MIDDLE,
                },
                tailEnd: {
                    lineEndType: LineEndType.ARROW,
                    lengthType: LineEndWidthType.MIDDLE,
                    widthType: LineEndWidthType.MIDDLE,
                },
                compoundType: CompoundType.SINGLE,
            },
        },
    })),
    ...colorList.map(color => ({
        title: `${i18n.t('双箭头')} ${color.name}`,
        type: ConnectorFastStyleType.doubleArrow,
        fastStyle: {
            effectRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fillRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
            fontRef: {
                egColorChoice: {
                    schemeClr: {
                        val: STSchemeColorVal.tx1,
                    },
                },
                idx: 2,
            },
            lnRef: {
                egColorChoice: {
                    scrgbClr: {
                        r: 0,
                        g: 0,
                        b: 0,
                    },
                },
                idx: 0,
            },
        },
        style: {
            border: {
                width: 3,
                linecap: ILineCap.Butt,
                style: 'solid',
                linejoin: LineJoin.Round,
                tdColor: new TDColor({ themeKey: color.val }),
                headEnd: {
                    lineEndType: LineEndType.ARROW,
                    lengthType: LineEndWidthType.MIDDLE,
                    widthType: LineEndWidthType.MIDDLE,
                },
                tailEnd: {
                    lineEndType: LineEndType.ARROW,
                    lengthType: LineEndWidthType.MIDDLE,
                    widthType: LineEndWidthType.MIDDLE,
                },
                compoundType: CompoundType.SINGLE,
            },
        },
    })),
];


